import React from "react";
import styled from "styled-components";
import {
  AlertTriangle,
  ArrowUpRight,
  Boxes,
  CheckCircle,
  Package,
  ShoppingBag,
  TrendingUp,
  Users,
  WalletCards,
} from "lucide-react";

const Page = styled.main`
  color: #172033;
`;

const Hero = styled.section`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
  padding: 26px 28px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);
  box-shadow: 0 12px 34px rgba(40, 102, 181, 0.07);
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #2f80ed;
  font-size: 13px;
  font-weight: 900;
`;

const Title = styled.h1`
  margin: 8px 0 8px;
  font-size: 30px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #66758f;
`;

const TodayBox = styled.div`
  min-width: 220px;
  padding: 16px;
  border: 1px solid #dce8f8;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.74);

  span {
    color: #66758f;
    font-size: 12px;
    font-weight: 800;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: #2f80ed;
    font-size: 24px;
  }
`;

const StatsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
`;

const StatCard = styled.article`
  padding: 20px;
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(32, 72, 122, 0.06);
`;

const StatTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  svg {
    color: #2f80ed;
  }
`;

const StatLabel = styled.div`
  margin-top: 16px;
  color: #66758f;
  font-size: 13px;
  font-weight: 800;
`;

const StatValue = styled.div`
  margin-top: 6px;
  color: #172033;
  font-size: 25px;
  font-weight: 900;
`;

const Trend = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  color: ${(props) => (props.$danger ? "#ef4444" : "#16a34a")};
  font-size: 12px;
  font-weight: 900;
`;

const MainGrid = styled.section`
  display: grid;
  grid-template-columns: 1.35fr 0.65fr;
  gap: 18px;
`;

const Panel = styled.section`
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 16px 36px rgba(32, 72, 122, 0.07);
  overflow: hidden;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid #e3ebf7;
  }

  h2 {
    margin: 0;
    font-size: 19px;
  }
`;

const OrderRow = styled.article`
  display: grid;
  grid-template-columns: 116px 1fr 130px 100px;
  gap: 14px;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #edf2f8;

  &:last-child {
    border-bottom: 0;
  }

  strong {
    color: #2f80ed;
  }

  span {
    color: #66758f;
    font-size: 13px;
  }
`;

const Status = styled.span`
  justify-self: start;
  border-radius: 999px;
  padding: 7px 10px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$fg};
  font-weight: 900;
`;

const TaskList = styled.div`
  display: grid;
  gap: 12px;
  padding: 18px;
`;

const Task = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: start;
  padding: 12px;
  border: 1px solid #edf2f8;
  border-radius: 8px;
  background: #f7fbff;

  svg {
    color: ${(props) => props.$danger ? "#ef4444" : "#2f80ed"};
  }

  strong {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
  }

  span {
    color: #66758f;
    font-size: 12px;
  }
`;

const stats = [
  { label: "오늘 주문", value: "128건", icon: ShoppingBag, trend: "+12.4%" },
  { label: "오늘 매출", value: "₩ 18.4M", icon: WalletCards, trend: "+8.1%" },
  { label: "신규 회원", value: "342명", icon: Users, trend: "+5.8%" },
  { label: "재고 경고", value: "7개", icon: Boxes, trend: "확인 필요", danger: true },
];

const recentOrders = [
  { id: "AN-10091", customer: "김민준", total: "₩ 2,350,000", status: "결제완료", bg: "#eaf3ff", fg: "#2f70cf" },
  { id: "AN-10090", customer: "이서연", total: "₩ 129,000", status: "배송중", bg: "#ecfdf5", fg: "#059669" },
  { id: "AN-10089", customer: "박지훈", total: "₩ 890,000", status: "상품준비", bg: "#fff7ed", fg: "#ea580c" },
  { id: "AN-10088", customer: "최유진", total: "₩ 1,650,000", status: "교환요청", bg: "#fee2e2", fg: "#dc2626" },
];

export default function AdminDashboard() {
  return (
    <Page>
      <Hero>
        <div>
          <Eyebrow><TrendingUp size={15} /> COMMERCE CONTROL</Eyebrow>
          <Title>관리자 대시보드</Title>
          <Subtitle>주문, 매출, 재고, 회원 현황을 빠르게 점검하세요.</Subtitle>
        </div>
        <TodayBox>
          <span>2026.05.03 운영 상태</span>
          <strong>정상 운영중</strong>
        </TodayBox>
      </Hero>

      <StatsGrid>
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <StatCard key={stat.label}>
              <StatTop>
                <Icon size={24} />
                <ArrowUpRight size={18} />
              </StatTop>
              <StatLabel>{stat.label}</StatLabel>
              <StatValue>{stat.value}</StatValue>
              <Trend $danger={stat.danger}>{stat.trend}</Trend>
            </StatCard>
          );
        })}
      </StatsGrid>

      <MainGrid>
        <Panel>
          <header>
            <h2>최근 주문</h2>
            <Status $bg="#eaf3ff" $fg="#2f70cf">실시간</Status>
          </header>
          {recentOrders.map((order) => (
            <OrderRow key={order.id}>
              <strong>{order.id}</strong>
              <span>{order.customer}</span>
              <b>{order.total}</b>
              <Status $bg={order.bg} $fg={order.fg}>{order.status}</Status>
            </OrderRow>
          ))}
        </Panel>

        <Panel>
          <header><h2>오늘 할 일</h2></header>
          <TaskList>
            <Task><CheckCircle size={20} /><div><strong>신규 주문 5건 확인</strong><span>결제완료 주문 출고 준비</span></div></Task>
            <Task $danger><AlertTriangle size={20} /><div><strong>재고 부족 7개</strong><span>인기 상품 우선 보충 필요</span></div></Task>
            <Task><Package size={20} /><div><strong>반품 요청 2건</strong><span>검수 후 환불 처리</span></div></Task>
          </TaskList>
        </Panel>
      </MainGrid>
    </Page>
  );
}
